import { useCallback } from 'react';

import { trackEvent } from '../actions/trackers';

export const isDebug = process.env.REACT_APP_DEBUG_UI === 'true';

const SESSION_ID_KEY = 'repairy_session_id';

const createSessionId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random()
    .toString(36)
    .slice(2)}`;

export const getOrCreateSessionId = () => {
  if (typeof window === 'undefined') return null;

  try {
    let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
    if (!sessionId) {
      sessionId = createSessionId();
      sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    }
    return sessionId;
  } catch (error) {
    if (isDebug) console.error('[useEventTracking] sessionStorage error', error);
    return createSessionId();
  }
};

export const useEventTracking = ({ shop } = {}) => {
  const track = useCallback(
    async (eventType, properties = {}) => {
      try {
        const sessionId = getOrCreateSessionId();
        if (!sessionId) {
          if (isDebug)
            console.warn('[useEventTracking] No sessionId available');
          return;
        }

        // Sanitize properties: convert snake_case to camelCase first
        const sanitizedProperties = { ...properties };
        Object.keys(sanitizedProperties).forEach((key) => {
          if (key.includes('_')) {
            // Convert snake_case to camelCase
            const camelCaseKey = key.replace(/_([a-z])/g, (_, letter) =>
              letter.toUpperCase(),
            );
            // If camelCase version doesn't exist, create it and remove snake_case
            if (!sanitizedProperties[camelCaseKey]) {
              sanitizedProperties[camelCaseKey] = sanitizedProperties[key];
            }
            delete sanitizedProperties[key];
          }
        });

        // Remove undefined values
        Object.keys(sanitizedProperties).forEach((key) => {
          if (sanitizedProperties[key] === undefined) {
            delete sanitizedProperties[key];
          }
        });

        // contextId: rwg token → fbclid → explicit id → sessionId
        const adsParameters = sanitizedProperties.adsParameters;
        const contextId =
          sanitizedProperties.rwgToken ||
          adsParameters?.fbclid ||
          sanitizedProperties.contextId ||
          sessionId;
        if (!contextId) {
          if (isDebug)
            console.warn('[useEventTracking] No contextId found in properties');
          return;
        }

         // Prepare event data with camelCase properties
         const eventData = {
          eventType,
          contextId,
          sessionId,
          pagePath: sanitizedProperties.pagePath || 'server',
          workshopId: sanitizedProperties.workshopId || shop?._id,
          source: sanitizedProperties.source || 'shortlink',
          properties: {
            ...sanitizedProperties,
            rwgToken: sanitizedProperties.rwgToken,
            utmSource: sanitizedProperties.utmSource,
            utmMedium: sanitizedProperties.utmMedium,
            utmCampaign: sanitizedProperties.utmCampaign,
            utmContent: sanitizedProperties.utmContent,
            utmTerm: sanitizedProperties.utmTerm,
            utmId: sanitizedProperties.utmId,
            adsParameters: sanitizedProperties.adsParameters,
            pagePath: sanitizedProperties.pagePath || 'server',
            pageTitle: sanitizedProperties.pageTitle || document?.title,
            shopCode: sanitizedProperties.shopCode || shop?.code,
            serviceReminderCode: sanitizedProperties.serviceReminderCode,
            feature: sanitizedProperties.feature,
          },
          context: {
            userAgent: navigator?.userAgent,
            referrer: document?.referrer,
          },
        };

        // Remove undefined values from final eventData properties
        Object.keys(eventData.properties).forEach((key) => {
          if (eventData.properties[key] === undefined) {
            delete eventData.properties[key];
          }
        });

        const ap = eventData.properties.adsParameters;
        if (ap && typeof ap === 'object' && Object.keys(ap).length === 0) {
          delete eventData.properties.adsParameters;
        }

        if (isDebug)
          console.log('[useEventTracking]', { eventType, eventData });

        await trackEvent(eventData);
      } catch (error) {
        // Handle errors gracefully - log but don't break user flow
        if (isDebug) console.error('[useEventTracking] Error', error);
      }
    },
    [shop],
  );

  return { track };
};
